import React, { useState, useEffect, useRef } from 'react';

export interface EditableTextProps {
  value: string;
  onChange?: (newVal: string) => void;
  isEditor?: boolean;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'div';
  className?: string;
  placeholder?: string;
  multiline?: boolean;
}

export function EditableText({
  value,
  onChange,
  isEditor = false,
  as = 'span',
  className = '',
  placeholder = 'Click to edit...',
  multiline = false,
}: EditableTextProps): React.JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(value || '');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setText(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleCommit = () => {
    setIsEditing(false);
    if (text !== value && onChange) {
      onChange(text);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (!multiline || (!e.shiftKey && !e.ctrlKey))) {
      e.preventDefault();
      handleCommit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setText(value || '');
    }
  };

  // If not in editor mode or no onChange handler provided, render standard semantic tag
  if (!isEditor || !onChange) {
    const Tag = as;
    return <Tag className={className}>{value || placeholder}</Tag>;
  }

  // Active inline editing input
  if (isEditing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          rows={2}
          className={`${className} outline-none ring-2 ring-primary rounded px-1.5 py-0.5 bg-background text-foreground resize-y w-full min-h-[40px]`}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    return (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleCommit}
        onKeyDown={handleKeyDown}
        className={`${className} outline-none ring-2 ring-primary rounded px-1.5 py-0.5 bg-background text-foreground inline-block min-w-[80px] max-w-full`}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  const Tag = as;
  return (
    <Tag
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      title="Click to edit text directly"
      className={`${className} cursor-text hover:outline-dashed hover:outline-1 hover:outline-primary/60 rounded transition-all`}
    >
      {value || <span className="opacity-40 italic">{placeholder}</span>}
    </Tag>
  );
}
